// src/admin/pages/tabs/DirectorsListTab.js
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, X, User, Building, Calendar, Mail, Phone } from 'lucide-react';
import { supabase } from '../../../config';
import EquityService from '../../../services/equityService';

const DirectorsListTab = () => {
  const [directors, setDirectors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDirector, setNewDirector] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_id: '',
    date_appointed: '',
    date_end: '',
    status: 'Active'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch directors with company info
      const { data: directorsData, error: directorsError } = await supabase
        .from('equity_directors')
        .select(`
          *,
          company:equity_companies!equity_directors_company_id_fkey(
            id,
            name,
            user_id
          )
        `)
        .order('created_at', { ascending: false });

      if (directorsError) throw directorsError;

      // Fetch companies
      const { data: companiesData, error: companiesError } = await supabase
        .from('equity_companies')
        .select('*')
        .order('created_at', { ascending: false });

      if (companiesError) throw companiesError;

      // Get unique user IDs from companies
      const userIds = [...new Set(companiesData.map(c => c.user_id).filter(Boolean))];
      
      // Fetch creator profiles
      let creators = {};
      if (userIds.length > 0) {
        const { data: creatorProfiles } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, email')
          .in('id', userIds);
        
        if (creatorProfiles) {
          creators = creatorProfiles.reduce((acc, profile) => {
            acc[profile.id] = profile;
            return acc;
          }, {});
        }
      }

      // Enrich directors with creator info
      const enrichedDirectors = (directorsData || []).map(director => ({
        ...director,
        company: director.company ? {
          ...director.company,
          creator: creators[director.company.user_id] || null
        } : null
      }));

      setDirectors(enrichedDirectors);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      setDirectors([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDirector = async () => {
    try {
      if (!newDirector.company_id) {
        alert('Please select a company');
        return;
      }
      if (!newDirector.first_name || !newDirector.last_name) {
        alert('First name and last name are required');
        return;
      }
      
      await EquityService.createDirector(newDirector);
      await loadData();
      setNewDirector({
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        phone: '',
        company_id: '',
        date_appointed: '',
        date_end: '',
        status: 'Active'
      });
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating director:', error);
      alert('Error creating director: ' + error.message);
    }
  };

  const handleDeleteDirector = async (id) => {
    if (!window.confirm('Are you sure you want to delete this director? This action cannot be undone.')) {
      return;
    }

    try {
      await EquityService.deleteDirector(id);
      await loadData();
    } catch (error) {
      console.error('Error deleting director:', error);
      alert('Error deleting director: ' + error.message);
    }
  };

  const getCompanyName = (director) => {
    if (director.company && typeof director.company === 'object') {
      return director.company.name || 'Unknown Company';
    }
    const companyId = typeof director === 'object' ? director.company_id : director;
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unknown Company';
  };

  const getStatusColor = (status) => {
    return status === 'Active' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading directors...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Directors Management</h2>
          <p className="text-gray-600 mt-1">Manage directors across all companies</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Director
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <User className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Directors</p>
              <p className="text-2xl font-bold text-gray-900">{directors.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Building className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Companies</p>
              <p className="text-2xl font-bold text-gray-900">{companies.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Directors</p>
              <p className="text-2xl font-bold text-gray-900">
                {directors.filter(d => d.status === 'Active').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Directors Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Director
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Appointed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creator
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {directors.map((director) => (
                <tr key={director.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {director.first_name} {director.middle_name ? director.middle_name + ' ' : ''}{director.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{director.email || 'No email'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Building className="h-4 w-4 text-gray-400 mr-1" />
                      {getCompanyName(director)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 text-gray-400 mr-1" />
                      {director.email || 'N/A'}
                    </div>
                    {director.phone && (
                      <div className="flex items-center text-gray-500">
                        <Phone className="h-4 w-4 text-gray-400 mr-1" />
                        {director.phone}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(director.status)}`}>
                      {director.status || 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {director.date_appointed 
                      ? new Date(director.date_appointed).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {director.company?.creator 
                      ? ((director.company.creator.first_name && director.company.creator.last_name)
                          ? `${director.company.creator.first_name} ${director.company.creator.last_name}`
                          : director.company.creator.first_name || director.company.creator.last_name || director.company.creator.email || 'Unknown')
                      : 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleDeleteDirector(director.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {directors.length === 0 && (
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No directors</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding a new director.</p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Director
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Director Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add New Director</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={(e) => { e.preventDefault(); handleCreateDirector(); }}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">First Name *</label>
                      <input
                        type="text"
                        required
                        value={newDirector.first_name}
                        onChange={(e) => setNewDirector({...newDirector, first_name: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                      <input
                        type="text"
                        required
                        value={newDirector.last_name}
                        onChange={(e) => setNewDirector({...newDirector, last_name: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Middle Name</label>
                    <input
                      type="text"
                      value={newDirector.middle_name}
                      onChange={(e) => setNewDirector({...newDirector, middle_name: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={newDirector.email}
                      onChange={(e) => setNewDirector({...newDirector, email: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={newDirector.phone}
                      onChange={(e) => setNewDirector({...newDirector, phone: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company *</label>
                    <select
                      required
                      value={newDirector.company_id}
                      onChange={(e) => setNewDirector({...newDirector, company_id: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Company</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Date Appointed</label>
                      <input
                        type="date"
                        value={newDirector.date_appointed}
                        onChange={(e) => setNewDirector({...newDirector, date_appointed: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <select
                        value={newDirector.status}
                        onChange={(e) => setNewDirector({...newDirector, status: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Add Director
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectorsListTab;

